import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  Autocomplete,
  Box,
  Chip,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import { ConfirmDialog } from '../../../../reusable-components/confirm-dialog';

const EMPTY_FORM = {
  facilities: [],
  vendors: [],
  approvers: [],
};

InvoiceApprovalModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  facilities: PropTypes.array,
  vendors: PropTypes.array,
  approvers: PropTypes.array,
  permissionType: PropTypes.string,
};

export default function InvoiceApprovalModal({
  open,
  onClose,
  onSave,
  onDelete,
  facilities,
  vendors,
  approvers,
  permissionType,
}) {
  const [value, setValue] = useState(EMPTY_FORM);
  const [tab, setTab] = useState(0); 
  const [submitting, setSubmitting] = useState(false); 
  useEffect(() => {
    setValue(EMPTY_FORM);
    setTab(0);
  }, [open]);

  const handleClose = () => {
    setValue(EMPTY_FORM);
    setTab(0);
    onClose();
  };

  const selectedFacilityIds = value.facilities.map((x) => x.id);
  const uniqueSelectedBookingTypeIds = [...new Set(value.facilities.map((x) => x.bookingTypeId))];

  const approverOptions = approvers
    .filter((a) => {
      if (!a.facilityIds || a.facilityIds.length === 0) return true;

      return selectedFacilityIds.every((id) => a.facilityIds.includes(id));
    })
    .map((x) => ({
      id: x.id,
      label: `${x.value} - ${x.id}`,
      value: x.value,
    }));

  const vendorOptions =
    uniqueSelectedBookingTypeIds.length === 1
      ? vendors
          .filter((v) => v.bookingTypeId === uniqueSelectedBookingTypeIds[0])
          .map((v) => ({ id: v.value, label: v.label }))
      : [];

  const handleSubmit = async (tab) => {
    if (submitting) return;

    try {
      setSubmitting(true);

      const payload = {
        facilityIds: value.facilities.map((x) => x.id),
        vendorIds: value.vendors.map((x) => x.id),
        approvers: value.approvers,
      };

      if (tab === 0) {
        await onSave(payload);
      } else {
        await onDelete(payload);
      }
    } finally {
      handleClose();
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={(event, reason) => {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      handleClose();
    }} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center' }}>
        Invoice Approvers
        <CloseIconButton onClick={handleClose}/>
      </DialogTitle>

      <DialogContent>
        <Tabs
          value={tab}
          onChange={(e, newValue) => setTab(newValue)}
          sx={{ mb: 2 }}
        >
          <Tab label="Add" />
          <Tab label="Remove" />
        </Tabs>

        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Box sx={{ width: '100%' }}>
              <Tooltip
                title={`You can only view facilities you have ${permissionType} permissions for`}
              >
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Facilities *
                </Typography>
              </Tooltip>

              <Autocomplete
                multiple
                disableCloseOnSelect
                fullWidth
                size="small"
                value={value.facilities}
                options={[
                  { id: -1, label: 'Select All...', all: true },
                  ...facilities.map((x) => ({
                    id: x.value,
                    label: x.label,
                  })),
                ]}
                onChange={(event, newValue) => {
                  if (newValue.some((o) => o.all)) {
                    setValue({
                      ...value,
                      facilities: facilities.map((x) => ({
                        id: x.value,
                        label: x.label,
                      })),
                    });
                    return;
                  }

                  setValue({
                    ...value,
                    facilities: newValue,
                  });
                }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, val) =>
                  option.id === val.id
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search or select facilities"
                  />
                )}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ width: '100%' }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Approvers *
              </Typography>

              <Autocomplete
                multiple
                disableCloseOnSelect
                fullWidth
                size="small"
                value={value.approvers}
                options={[
                  { id: -1, label: 'Select All...', all: true },
                  ...approverOptions,
                ]}
                onChange={(event, newValue) => {
                  setValue({
                    ...value,
                    approvers: newValue.some((o) => o.all)
                      ? approverOptions
                      : newValue,
                  });
                }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, val) =>
                  option.id === val.id
                }
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={`${index + 1}. ${option.id}`}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search or select approvers"
                  />
                )}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ width: '100%' }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Vendors
              </Typography>

              <Autocomplete
                multiple
                disableCloseOnSelect
                fullWidth
                size="small"
                value={value.vendors}
                options={vendorOptions.length > 1 ?[
                  { id: -1, label: 'Select All...', all: true },
                  ...vendorOptions,
                ] : vendorOptions}
                onChange={(event, newValue) => {
                  setValue({
                    ...value,
                    vendors: newValue.some((o) => o.all)
                      ? vendorOptions
                      : newValue,
                  });
                }}
                getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, val) =>
                option.id === val.id
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search or select vendors"
                />
              )}
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          variant="outlined"
          color="error"
          onClick={handleClose}
          sx={{ width: 150 }}
        >
          Cancel
        </Button>

        <ConfirmDialog
          icon={tab === 0 ? "add" : "delete-outline"}
          title={tab === 0 ? "Add Invoice Approvers" : "Remove Invoice Approvers"}
          content={
            tab === 0
              ? "Are you sure you want to add the selected facility vendor approvers? This will add them to the pending invoices."
              : "Are you sure you want to remove the selected facility vendor approvers? This will remove them from the pending invoices"
          }
          iconSx={{ width: 20 }} 
          actionButton={tab === 0 ? "Add" : "Remove"}
          button="contained"
          color={tab === 0 ? "primary" : "error"}
          disabled={submitting||
            !value.facilities.length || !value.approvers.length
          }
          buttonStyles={{ width: 150 }}
          action={() => handleSubmit(tab)}
        />
      </DialogActions>
    </Dialog>
  );
}